import { TableDataItem } from '~/components/ui/tables/Table';
import { NestedHome } from '@habity/types';
import { isHouse, isCottage, isHousingCooperative, isPlot, VitecEstateType, isFarm, isCommercialProperty, isCondominium } from '@habity/types/vitec';
import { filterProviderData } from './utils';
import { IconType } from 'react-icons';

export type DataFieldConfig = {
  key: string;
  label: string | ((context: DataBuilderContext) => string);
  icon?: IconType;
  getValue?: (context: DataBuilderContext) => any;
  formatter?: (value: any, context?: any) => string;
  condition?: (context: DataBuilderContext) => boolean;
  estateTypes?: VitecEstateType[];
  source?: 'provider' | 'address' | 'filtered';
};

export type DataBuilderContext = {
  home: NestedHome;
  provider_data: any;
  provider_type: VitecEstateType | null | undefined;
  filteredProviderData: any;
  address: any;
  isApartment: boolean;
};

export class HomeDataBuilder {
  private context?: DataBuilderContext;
  private data: TableDataItem[] = [];
  private fields: DataFieldConfig[] = [];

  constructor(home?: NestedHome) {
    if (home) {
      this.initializeContext(home);
    }
  }

  private initializeContext(home: NestedHome) {
    const { address, provider_data, provider_type } = home || {};
    const filteredProviderData = provider_data ? filterProviderData(provider_data) : null;
    const isApartment = address?.category === "apartment";

    this.context = {
      home,
      provider_data,
      provider_type: provider_type || null,
      filteredProviderData,
      address,
      isApartment
    };
  }

  withFields(fields: DataFieldConfig[]): this {
    this.fields = fields;
    return this;
  }

  private getValue(field: DataFieldConfig): any {
    if (!this.context) return null;
    
    // Use custom getValue function if provided
    if (field.getValue) {
      return field.getValue(this.context);
    }
    
    const { key, source = 'filtered' } = field;
    const keys = key.split('.');
    
    let sourceData;
    switch (source) {
      case 'provider':
        sourceData = this.context.provider_data;
        break;
      case 'address':
        sourceData = this.context.address;
        break;
      case 'filtered':
      default:
        sourceData = this.context.filteredProviderData;
        break;
    }

    return keys.reduce((obj, k) => obj?.[k], sourceData);
  }

  private shouldIncludeField(field: DataFieldConfig): boolean {
    if (!this.context) return false;
    
    const { condition, estateTypes } = field;
    
    // Check estate type restriction
    if (estateTypes && estateTypes.length > 0 && this.context.provider_type) {
      const matchesType = estateTypes.some(type => {
        switch (type) {
          case 'House':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isHouse(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'Cottage':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isCottage(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'HousingCooperative':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isHousingCooperative(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'Plot':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isPlot(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'Farm':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isFarm(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'CommercialProperty':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isCommercialProperty(this.context.filteredProviderData, this.context.provider_type) : false;
          case 'Condominium':
            return this.context?.filteredProviderData && this.context?.provider_type ? 
              isCondominium(this.context.filteredProviderData, this.context.provider_type) : false;
          default:
            return false;
        }
      });
      if (!matchesType) return false;
    }

    // Check custom condition
    if (condition) {
      return condition(this.context);
    }

    // Default: include if value exists
    const value = this.getValue(field);
    return value !== null && value !== undefined && value !== '';
  }

  addField(field: DataFieldConfig): this {
    if (!this.shouldIncludeField(field)) return this;

    const value = this.getValue(field);
    const formattedValue = field.formatter ? field.formatter(value, this.context) : value;

    const label = typeof field.label === 'function' && this.context ? field.label(this.context) : (typeof field.label === 'string' ? field.label : '');

    this.data.push({
      ...(field.icon && { icon: field.icon }),
      label,
      value: formattedValue,
    });

    return this;
  }

  addFields(fields: DataFieldConfig[]): this {
    fields.forEach(field => this.addField(field));
    return this;
  }

  addConditionalField(
    condition: boolean,
    field: DataFieldConfig | (() => DataFieldConfig)
  ): this {
    if (condition) {
      const fieldConfig = typeof field === 'function' ? field() : field;
      this.addField(fieldConfig);
    }
    return this;
  }

  addCustomField(item: TableDataItem): this {
    this.data.push(item);
    return this;
  }

  build(context?: { home: NestedHome }): TableDataItem[] {
    if (context && !this.context) {
      this.initializeContext(context.home);
    }
    
    // Process fields if they were added via withFields
    if (this.fields.length > 0) {
      this.addFields(this.fields);
    }
    
    return this.data;
  }

  getContext(): DataBuilderContext {
    if (!this.context) {
      throw new Error('Context not initialized. Call build() with a home parameter first.');
    }
    return this.context;
  }
}

// Helper function to create estate type conditions
export const forEstateTypes = (...types: VitecEstateType[]) => types;

// Helper function to create value existence condition
export const ifExists = (condition?: (value: any, context: DataBuilderContext) => boolean) => 
  condition || ((value: any) => value !== null && value !== undefined && value !== '');

// Helper function to create complex conditions
export const ifCondition = (condition: (value: any, context: DataBuilderContext) => boolean) => condition;
